import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Divider, IconButton, Stack, Typography, CircularProgress, styled,
  } from "@mui/material";
  import WarningAmberIcon from "@mui/icons-material/WarningAmber";
  import CloseIcon from "@mui/icons-material/Close";
  import { CustomButton } from "@/components/custom/custom-button";
  import { CustomErrorMessage } from "@/components/custom/custom-error-message";
  
  const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialog-paper": {
      borderRadius: "12px",
      padding: theme.spacing(1),
      maxWidth: 460,
      width: "100%",
    },
  }));
  
  const IconWrapper = styled(Box)(() => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 44,
    height: 44,
    borderRadius: "10px",
    flexShrink: 0,
    backgroundColor: "#fef3c7",
  }));
  
  const resolveErrorMessage = (error) => {
    const detail = Array.isArray(error?.details) ? error.details[0] : null;
    return detail ?? error?.message ?? "Settlement could not be processed. Please try again.";
  };
  
  // `rows` is [{ label, value }] so the transactions and settlements modules can
  // each show their own fields through one dialog.
  const ManualSettlementDialog = ({
    open, onClose, reference, rows = [], notice, mutation, onConfirm,
  }) => {
    const isPending = mutation?.isPending;
  
    const handleClose = () => {
      if (isPending) return;
      mutation?.reset?.();
      onClose();
    };
  
    return (
      <StyledDialog open={open} onClose={handleClose}>
        <DialogContent sx={{ pt: 2, px: 2, pb: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <IconWrapper>
              <WarningAmberIcon sx={{ fontSize: 22, color: "#d97706" }} />
            </IconWrapper>
  
            <Box sx={{ flex: 1 }}>
              <DialogTitle sx={{ p: 0, fontSize: "1rem", fontWeight: 600 }}>
                Settle manually
              </DialogTitle>
              <Typography variant="caption" color="text.secondary">
                This moves funds to the merchant outside the automatic settlement run and cannot be undone here.
              </Typography>
            </Box>
  
            <IconButton
              size="small"
              onClick={handleClose}
              disabled={isPending}
              sx={{ alignSelf: "flex-start", mt: -0.5, mr: -0.5, color: "text.secondary" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
  
          <Divider />
  
          <Stack
            spacing={1}
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: "grey.50",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            {rows.map(({ label, value }) => (
              <Stack key={label} direction="row" justifyContent="space-between" spacing={2}>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  sx={{ textAlign: "right", wordBreak: "break-word" }}
                >
                  {value ?? "N/A"}
                </Typography>
              </Stack>
            ))}
  
            {notice && (
              <Typography variant="caption" sx={{ color: "#92400e", pt: 0.5 }}>
                {notice}
              </Typography>
            )}
          </Stack>
  
          {mutation?.isError && (
            <CustomErrorMessage message={resolveErrorMessage(mutation?.error)} />
          )}
        </DialogContent>
  
        <DialogActions sx={{ px: 2, py: 2, gap: 1 }}>
          <CustomButton variantType="outline" size="small" onClick={handleClose} disabled={isPending}>
            Cancel
          </CustomButton>
          <CustomButton
            size="small"
            onClick={() => onConfirm(reference)}
            disabled={isPending || !reference || notice}
            sx={{ minWidth: 150 }}
          >
            {isPending ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={14} sx={{ color: "#fff" }} />
                Settling...
              </Box>
            ) : (
              "Process settlement"
            )}
          </CustomButton>
        </DialogActions>
      </StyledDialog>
    );
  };
  
  export default ManualSettlementDialog;